/* eslint-disable id-length */
import flatten from 'lodash/flatten';
import size from 'lodash/size';
import filter from 'lodash/filter';
import union from 'lodash/union';
import _some from 'lodash/some';
import _startsWith from 'lodash/startsWith';
import _toLower from 'lodash/toLower';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import isEmpty from 'lodash/isEmpty';
import { updateChoiceSuccess } from '@gcc/configurator/dist/actions/choiceActions';
import {
  SET_SURFACE,
  SET_CURRENT_SKU,
  SET_COUNTERTOP_SURFACES,
  SET_PRODUCT_AVAILABILITY,
  UPDATE_COLOR_SWATCHES,
  GET_COUNTERTOP_SURFACES_PENDING,
  GET_COUNTERTOP_SURFACES_FULFILLED,
  GET_COUNTERTOP_SURFACES_FAILED,
} from './actionTypes';
import surfaceAvailability from '../api/surfaceAvailability';
import { enableSave, disableSave } from './saveActions';
import { setSelectedColor } from './colorActions';
import loadConfiguratorOptionsBySku from './configurator/loadConfiguratorOptionsBySku';
import ColorUtils from '../utils/color-utils';
import log from '../api/logApi';
import { SwatchObjPropertyPaths } from '../common/constants';
import { Utils } from '../utils/urlUtils';
import { findPlaSurfaceCode, findPlaSelectedThickness } from '../utils/plaConfigurationUtils';
import { setPageErrorInfo } from '../utils/analytics/digitalDataBuilder';
import {
  choiceCustomOrPostform,
  customOrPostformSkus,
  customOrPostformSurfaceCodes,
  getLaminateColorChoices,
  handleLaminateColorGroup
} from '../utils/postform/postFormUtils';
import logNewRelicCustom, { LAMINATE_COLOR_MISSING_IN_CLASSIFICATION } from '../utils/logNewRelicCustom';

export const setCurrentSku = (sku) => async (dispatch, getState) => {
  const { currentSurface } = getState().countertops;
  const { plaConfigurationDetails } = await getState().plaConfiguration;

  if (Utils.hasPlaGuid() && plaConfigurationDetails) {
    const plaSurfaceCode = findPlaSurfaceCode(plaConfigurationDetails);
    const plaThickness = findPlaSelectedThickness(plaConfigurationDetails);
    dispatch({
      type: SET_CURRENT_SKU,
      sku: Number(plaSurfaceCode),
      height: plaThickness,
    });
  } else {
    const currentSku = (currentSurface.skus || []).find((ss) => ss.sku === sku);
    dispatch({
      type: SET_CURRENT_SKU,
      sku,
      height: (currentSku || {}).height,
    });
  }

};

const getSku = (surfaces, configSkuNumber, selectedColor) => {
  const checkThreeCm = (sku) => Number(sku.height) === 3;
  const availableSkus = surfaces
    .flatMap((ss) => (ss.skus.length > 0 ? ss.skus.map((sk) => sk.sku).join('-') : []))
    .join(',');
  // check for existing config sku or pick 3cm sku if availbale.
  const surface = surfaces.find((ss) => ss.skus.find((sku) => sku.sku === +configSkuNumber)
    || (ss.surfaceCode === +selectedColor.surfaceCode));

  let sku = '';
  if (+configSkuNumber > 0) {
    sku = +configSkuNumber;
  } else {
    const { skus } = surface;
    sku = selectedColor.sku;
    if (skus.length === 1) {
      [{ sku }] = skus;
    } else {
      const threeCm = skus.find((ss) => checkThreeCm(ss));
      if (threeCm) {
        ({ sku } = threeCm);
      } else {
        [{ sku }] = skus;
      }
    }
  }
  return { sku, surface, availableSkus };
};

const setSurfaceCode = (currentSurfaceCodes, configSkuNumber) => (dispatch, getState) => {
  const {
    surfaces,
    currentSurface,
    selectedSku,
    selectedColor,
    availableSkus: existingavailableSkus,
  } = getState().countertops;

  const availableSurfaces = surfaces.filter((ss) => currentSurfaceCodes.split(',').includes(`${ss.surfaceCode}`));

  if (!availableSurfaces || !availableSurfaces.length) {
    dispatch({
      type: SET_PRODUCT_AVAILABILITY,
      availability: false,
    });
    dispatch(disableSave());
    return;
  }

  dispatch({
    type: SET_PRODUCT_AVAILABILITY,
    availability: true,
  });
  dispatch(enableSave());

  if (!selectedSku) {
    const { sku, surface, availableSkus } = getSku(availableSurfaces, configSkuNumber, selectedColor);

    if (
      (currentSurface && surface.surfaceCode === currentSurface.surfaceCode)
      && (selectedSku === sku)
      && (existingavailableSkus === availableSkus)
    ) {
      return;
    }

    dispatch({
      type: SET_SURFACE,
      surface,
      availableSkus,
      currentSurfaceCodes,
    });

    dispatch(setCurrentSku(sku));
  }

};

const updateColorPrices = (dispatch, getState, allSurfaceColors) => {
  const state = getState();
  const { currentColorSwatches, currentOmsId } = state.products;
  const { laminatePostFormFeatureGate } = state.countertops;
  const colorSwatches = currentColorSwatches[currentOmsId] || [];
  const updatedColors = [];
  colorSwatches.forEach((cc) => {
    const colors = allSurfaceColors.filter((ss) => ss.price > 0).filter((xx) => xx.optionId === cc.externalId);
    if (colors && colors.length > 0) {
      const selectedColor = colors.find((color) =>
        laminatePostFormFeatureGate && color?.postFormSurfaceCode) ||
        colors.sort((aa, bb) => aa.price - bb.price)[0];

      updatedColors.push({
        ...cc,
        price: selectedColor.price,
        originalPrice: selectedColor.oldPrice || selectedColor.price,
        surfaceCode: selectedColor.surfaceCode,
      });
    }
  });

  const featureSortedColors = ColorUtils.featureSort(
    updatedColors,
    SwatchObjPropertyPaths.name,
    SwatchObjPropertyPaths.imageUrl,
  );

  if (isEmpty(state.countertops.selectedColor) && (window.location.href.indexOf('item') === -1)) {
    dispatch(setSelectedColor(featureSortedColors[0]));
  }

  dispatch({
    type: UPDATE_COLOR_SWATCHES,
    payload: featureSortedColors,
  });
};

export const setSurfacesWithColors = (data) => (dispatch, getState) => {
  const allSurfaceColors = filter(
    flatten(data.map((xx) => xx.skus
      .reduce((acc, ss) => union(acc, ss.colorList
        .map((cc) => ({
          ...cc,
          sku: ss.sku,
          height: ss.height,
          surfaceCode: xx.surfaceCode,
          brand: xx.desc,
          postFormSurfaceCode: xx.postFormSurfaceCode,
        }))), []))),
    size,
  );

  dispatch({
    type: SET_COUNTERTOP_SURFACES,
    payload: { surfaceCodes: data, allSurfaceColors },
  });

  // updates prices for color swatches
  updateColorPrices(dispatch, getState, allSurfaceColors);
};

export const getSurfaces = (storeId, surfaceCodes, configSkuNumber) => (dispatch, getState) => {
  dispatch({
    type: GET_COUNTERTOP_SURFACES_PENDING,
  });

  return surfaceAvailability(storeId, surfaceCodes, getState)
    .then((response) => {
      dispatch(setSurfacesWithColors(response));
      dispatch(setSurfaceCode(surfaceCodes, configSkuNumber));
      return response;
    })
    .then(() => {
      dispatch({
        type: GET_COUNTERTOP_SURFACES_FULFILLED,
      });
    })
    .catch((error) => {
      setPageErrorInfo({
        type: 'system',
        displayType: 'page',
        pageSection: 'countertops surfaceAvailability API',
        message: error?.message || 'An unexpected error occurred',
        code: error?.code || 'UNKNOWN_ERROR'
      });
      log(error);
      dispatch({
        type: GET_COUNTERTOP_SURFACES_FAILED,
      });
    });
};

export const setColorAndCurrentSurface = (choice, onChoiceSelected) => (
  dispatch,
  getState,
) => {
  const {
    countertops: { surfaces, selectedSku, allSurfaceColors, selectedThickness, selectedColor, currentSurfaceCodes, laminatePostFormFeatureGate },
    configurator: { options, choices },
    draftingTool: { designClassification, shapes },
    design: { configurationSaved },
    store: { storeNumber },
    config: { productId }
  } = getState();

  const isDesignClassified = designClassification === 'postform' || designClassification === 'custom';
  const isEditFlow = configurationSaved;

  let updatedChoice = { ...choice };

  if (isDesignClassified) {
    const choiceCustomOrPostForm = choiceCustomOrPostform(choice, allSurfaceColors, designClassification);
    updatedChoice = { ...updatedChoice, ...choiceCustomOrPostForm };

    if (isEmpty(selectedColor) && isEditFlow) {
      dispatch(setSelectedColor(updatedChoice));
    }
  }

  if (isEmpty(selectedColor) && configurationSaved) {
    dispatch(setSelectedColor(updatedChoice));
  }

  const surface = surfaces.find((s) => Number(updatedChoice.surfaceCode) === s.surfaceCode);

  const surfaceSkus = surface?.skus[0] || [];

  const selectedColorExternalId = updatedChoice?.externalId;
  const hasSelectedColorInSurfaceSkus = Array.isArray(surfaceSkus.colorList)
    ? surfaceSkus.colorList.some((color) => color.optionId === selectedColorExternalId)
    : false;

  if (!hasSelectedColorInSurfaceSkus && laminatePostFormFeatureGate) {
    const url = window?.location.href;
    // deselects choice if not available when classification switches
    // Future story to pick default color from custom available choices
    dispatch(setSelectedColor({}));
    dispatch(updateChoiceSuccess(updatedChoice.optionId, updatedChoice.id, updatedChoice.shortLabel));
    dispatch(disableSave());
    logNewRelicCustom(LAMINATE_COLOR_MISSING_IN_CLASSIFICATION, {
      url,
      color: updatedChoice.shortLabel,
      colorId: updatedChoice.id,
      optionId: updatedChoice.optionId,
      missingFromClassification: designClassification,
      storeNumber
    });

    dispatch(getLaminateColorChoices(designClassification));
    return;
  }

  const availableSkuThicknessesForChoice = surface.skus.filter((sku) =>
    updatedChoice.skuNumbers.includes(sku.sku)
  );
  const choiceHasCurrentThicknessSkuAvailable = !!availableSkuThicknessesForChoice.find(
    (c) => c.sku === selectedSku
  );

  // Fallback: Use externalId to find a match in the colorList if no skus are found
  // e.g. switching stores where choice has different sku
  if (availableSkuThicknessesForChoice.length === 0) {
    const externalId = Number(updatedChoice.externalId);
    const fallbackSkus = surface.skus.filter((sku) =>
      sku.colorList.some((color) => color.optionId === externalId)
    );

    if (fallbackSkus.length > 0) {
      availableSkuThicknessesForChoice.push(...fallbackSkus);
    }
  }

  if (choiceHasCurrentThicknessSkuAvailable) {
    onChoiceSelected(updatedChoice.optionId, updatedChoice.id, updatedChoice.shortLabel);
    handleLaminateColorGroup(
      { designClassification,
        shapes,
        options,
        selectedColor: updatedChoice,
        choices,
        onChoiceSelected,
        laminatePostFormFeatureGate,
        productId
      }
    );
  } else {
    const defaultSkuChoice =
      availableSkuThicknessesForChoice.find((c) => c.height === selectedThickness) ||
      availableSkuThicknessesForChoice[0];

    const updatedSkus = customOrPostformSkus(allSurfaceColors, designClassification);

    dispatch({
      type: SET_SURFACE,
      availableSkus: updatedSkus,
      surface,
      currentSurfaceCodes
    });

    let currentSku = defaultSkuChoice?.sku;
    if (isDesignClassified) {
      currentSku =
        designClassification === 'custom'
          ? updatedChoice.customSku[0]
          : updatedChoice.postFormSku[0];
    }

    dispatch({
      type: SET_CURRENT_SKU,
      sku: currentSku,
      height: defaultSkuChoice?.height || surface.skus[0]?.height,
    });

    if (!updatedChoice.isSelected) {
      dispatch(updateChoiceSuccess(updatedChoice.optionId, updatedChoice.id, updatedChoice.shortLabel));
      dispatch(setSelectedColor(updatedChoice));
    }

    const onChoicesLoaded = () => {
      handleLaminateColorGroup(
        { designClassification,
          shapes,
          options: getState().configurator.options,
          selectedColor: updatedChoice,
          choices: getState().configurator.choices,
          onChoiceSelected,
          laminatePostFormFeatureGate,
          productId
        }
      );
    };

    dispatch(loadConfiguratorOptionsBySku(currentSku, onChoicesLoaded));
  }
};
